import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Container, Grid, Card, CardContent, Avatar, Box, Link as MuiLink, List, ListItem, ListItemIcon, ListItemText, Chip, ThemeProvider, createTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MessageCircle, BrainCircuit, Rocket, Code, FileText, Users, Zap, Database, Settings, Cpu, Cloud, Layers, Network, Bot, Search, Check, Shield, Lock, Server } from 'lucide-react';
import { useKeycloak } from '@react-keycloak/web';
import PageLoading from '../helpers/PageLoading';
import { whiteLabelContent } from '../landingpage/WhiteLabelLandingPageContent';
import { whiteLabelData } from '../helpers/WhiteLabelData';

const WhiteLabelLandingPage = () => {
  const { keycloak, initialized } = useKeycloak();
  const [language, setLanguage] = useState('en');
  const [clientData, setClientData] = useState(null);
  const [content, setContent] = useState(null);

  useEffect(() => {
    // Function to get the subdomain from the hostname
    const getSubdomain = () => {
      const hostname = window.location.hostname;
      // If localhost, use a default value for testing
      if (hostname === 'localhost') return 'cloudiogram'; // Default for local testing
      
      const parts = hostname.split('.');
      if (parts.length >= 3) {
        return parts[0]; // Return the subdomain part
      }
      return null; // No subdomain found
    };

    // Find client data based on subdomain
    const subdomain = getSubdomain();
    const matchingClient = whiteLabelData.find(client => 
      client.domain.startsWith(subdomain) || client.client_id === subdomain
    );
    
    if (matchingClient) {
      // Use client data directly from whiteLabelData.js
      setClientData(matchingClient);
      
      // Set page title and favicon
      document.title = matchingClient.title || 'Enterprise AI Platform';
      if (matchingClient.favicon_url) {
        const favicon = document.getElementById('favicon') || document.createElement('link');
        favicon.id = 'favicon';
        favicon.rel = 'shortcut icon';
        favicon.href = matchingClient.favicon_url;
        document.head.appendChild(favicon);
      }
    } else {
      // Fallback to default branding if no matching client
      setClientData({
        name: 'Enterprise AI',
        logo_url: '../../images/pv_white.png',
        theme: {
          primary_color: '#3E7BFA',
          secondary_color: '#6ABFFF',
          background_color: '#0A1929',
          text_color: '#FFFFFF',
          accent_color: '#00C896'
        },
        font: {
          family: 'Inter, Roboto, sans-serif'
        },
        powered_by: 'Powered by PioSphere'
      });
    }

    // Detect language
    fetch('https://ipapi.co/json/')
      .then(response => response.json())
      .then(data => {
        setLanguage(data.country_code === 'DE' ? 'de' : 'en');
      })
      .catch(error => {
        console.error('Error detecting country:', error);
        // Default to English if language detection fails
        setLanguage('en');
      });
  }, []);

  useEffect(() => {
    if (language) {
      setContent(whiteLabelContent[language]);
    }
  }, [language]);

  if (!initialized) {
    return <PageLoading />;
  }

  if (keycloak.authenticated) {
    return <Navigate to="/home" replace />;
  }

  if (!clientData || !content) {
    return <PageLoading />;
  }

  // Create a theme based on client data
  const theme = createTheme({
    palette: {
      mode: clientData.theme.background_color === '#FFFFFF' ? 'light' : 'dark',
      primary: {
        main: clientData.theme.primary_color,
      },
      secondary: {
        main: clientData.theme.secondary_color,
      },
      background: {
        default: clientData.theme.background_color || '#0A1929',
        paper: clientData.theme.background_color === '#FFFFFF' 
          ? '#F8F9FA' 
          : clientData.theme.background_color 
            ? clientData.theme.background_color.replace(/#/, '#1') 
            : '#132F4C',
      },
      text: {
        primary: clientData.theme.text_color || '#FFFFFF',
        secondary: clientData.theme.text_color === '#000000'
          ? '#666666'
          : clientData.theme.text_color 
            ? `${clientData.theme.text_color}99` 
            : '#B0B0B0',
      },
      accent: {
        main: clientData.theme.accent_color || '#00C896',
      },
      divider: clientData.theme.background_color === '#FFFFFF' ? '#E0E0E0' : undefined,
    },
    typography: {
      fontFamily: clientData.theme.font?.family || 'Inter, Roboto, sans-serif',
      h1: {
        fontWeight: 800,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 700,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontWeight: 700,
      },
      h5: {
        fontWeight: 500,
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: 600,
      },
      button: {
        fontWeight: 600,
      }
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
    },
  });

  const StyledButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    padding: '12px 24px',
    fontWeight: 600,
    fontSize: '1rem',
  }));

  const FeatureCard = styled(Card)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: `0 12px 24px ${theme.palette.common.black}30`
    },
    overflow: 'hidden',
  }));

  const BackgroundGradient = styled('div')(({ theme }) => ({
    position: 'absolute',
    zIndex: 0,
    width: '100%',
    height: '100vh',
    top: 0,
    left: 0,
    background: `radial-gradient(circle at 50% 0%, ${theme.palette.primary.main}15, transparent 70%)`,
    pointerEvents: 'none',
  }));

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'de' : 'en');
  };

  const getIconComponent = (iconName, props) => {
    const iconMap = {
      brainCircuit: BrainCircuit,
      code: Code,
      rocket: Rocket,
      messageCircle: MessageCircle,
      fileText: FileText,
      users: Users,
      bot: Bot,
      search: Search,
      settings: Settings,
      cpu: Cpu,
      cloud: Cloud,
      layers: Layers,
      network: Network,
      database: Database,
      shield: Shield,
      lock: Lock,
      server: Server,
      zap: Zap
    };
    
    const IconComponent = iconMap[iconName] || BrainCircuit;
    return <IconComponent {...props} />;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        overflowX: 'hidden',
        bgcolor: 'background.default',
        top: 0
      }}>
        <Box sx={{ 
          minHeight: '100vh', 
          bgcolor: 'background.default', 
          color: 'text.primary',
          position: 'relative'
        }}>
          <BackgroundGradient />
          
          <AppBar 
            position="absolute" 
            color="transparent" 
            elevation={0} 
            sx={{ 
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              backgroundColor: theme.palette.mode === 'light' 
                ? 'rgba(255, 255, 255, 0.8)' 
                : 'rgba(0, 0, 0, 0.5)',
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
              zIndex: 10,
            }}
          >
            <Container maxWidth="xl">
              <Toolbar sx={{ justifyContent: 'space-between', px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img 
                    src={clientData.logo_url || "../../images/pv_white.png"} 
                    alt={`${clientData.name} Logo`} 
                    style={{ 
                      height: '40px',
                      borderRadius: clientData.client_id === 'cloudiogram' ? '8px' : '0',
                      objectFit: 'contain'
                    }} 
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="div" 
                      sx={{ 
                        display: { xs: 'none', md: 'block' },
                        fontWeight: 600
                      }}
                    >
                      {clientData.name}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: { xs: 'none', md: 'block' },
                        opacity: 0.7,
                        fontSize: '0.7rem',
                        mt: -0.5
                      }}
                    >
                      {clientData.powered_by}
                    </Typography>
                  </Box>
                </Box>
                
                <Box 
                  sx={{ 
                    display: { xs: 'none', lg: 'flex' }, 
                    justifyContent: 'center', 
                    flexGrow: 1,
                    gap: 4
                  }}
                >
                  <MuiLink 
                    href="#features"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('features');
                      if (element) {
                        const offset = 80;
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - offset;
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth'
                        });
                      }
                    }}
                    color="inherit" 
                    sx={{ 
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    {content.nav.features}
                  </MuiLink>
                  
                  <MuiLink 
                    href="#solutions"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('solutions');
                      if (element) {
                        const offset = 80;
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - offset;
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth'
                        });
                      }
                    }}
                    color="inherit" 
                    sx={{ 
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    {content.nav.solutions}
                  </MuiLink>
                  
                  <MuiLink 
                    href="#security"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('security');
                      if (element) {
                        const offset = 80;
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - offset;
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth'
                        });
                      }
                    }}
                    color="inherit" 
                    sx={{ 
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    {content.nav.security}
                  </MuiLink>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button 
                    variant="text"
                    onClick={toggleLanguage}
                    sx={{ 
                      color: theme.palette.mode === 'light' ? 'text.primary' : 'common.white',
                      minWidth: { xs: '40px', sm: 'auto' },
                      px: { xs: 1, sm: 2 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <img 
                      src={`https://flagcdn.com/${language === 'en' ? 'de' : 'gb'}.svg`}
                      alt={language === 'en' ? 'German flag' : 'British flag'}
                      style={{
                        width: '24px',
                        height: '16px',
                        borderRadius: '2px',
                        objectFit: 'cover'
                      }}
                    />
                    {language === 'en' ? 'DE' : 'EN'}
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={() => keycloak.login()}
                    sx={{ 
                      ml: 1,
                      color: theme.palette.mode === 'light' ? 'text.primary' : 'common.white', 
                      borderColor: theme.palette.mode === 'light' ? 'text.primary' : 'common.white',
                      display: { xs: 'none', sm: 'inline-flex' }
                    }}
                  >
                    {content.nav.login}
                  </Button>
                  <Button 
                    variant="contained"
                    component={Link} 
                    to="/register"
                    sx={{ 
                        ml: 1,
                        display: { xs: 'none', sm: 'inline-flex' }
                      }}
                  >
                    {content.hero.cta}
                  </Button>
                </Box>
              </Toolbar>
            </Container>
          </AppBar>

          {/* Hero Section */}
          <Box sx={{ 
            position: 'relative',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            zIndex: 1
          }}>
            <Container maxWidth="lg">
              <Typography 
                variant="h1" 
                component="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                  mb: 3,
                  background: theme.palette.mode === 'light'
                    ? `linear-gradient(135deg, ${clientData.theme.text_color || '#000000'}, ${clientData.theme.accent_color || '#4A4A4A'})`
                    : `linear-gradient(135deg, ${clientData.theme.text_color || '#FFFFFF'}, ${clientData.theme.secondary_color || '#6ABFFF'})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                {content.hero.title}
              </Typography>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 6, 
                  maxWidth: '800px', 
                  mx: 'auto',
                  color: 'text.secondary'
                }}
              >
                {content.hero.subtitle}
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'center',
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <StyledButton 
                  variant="contained"
                  component={Link} 
                  to="/register"
                  size="large"
                  sx={{ px: 4, py: 1.5 }}
                >
                  {content.hero.cta}
                </StyledButton>
                
                <Button 
                  variant="outlined" 
                  onClick={() => keycloak.login()}
                  size="large"
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2
                    }
                  }}
                >
                  {content.hero.login}
                </Button>
              </Box>
            </Container>
          </Box>

          {/* Features Section */}
          <Box 
            id="features"
            sx={{ 
              py: 10,
              position: 'relative',
              background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 100%)'
            }}
          >
            <Container maxWidth="lg">
              <Typography 
                variant="h2" 
                align="center" 
                sx={{ 
                  mb: 2,
                  background: `linear-gradient(45deg, ${clientData.theme.primary_color}, ${clientData.theme.secondary_color || clientData.theme.primary_color})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                {content.features.title}
              </Typography>
              
              <Typography 
                variant="h5" 
                align="center" 
                color="text.secondary" 
                sx={{ mb: 8 }}
              >
                {content.features.subtitle}
              </Typography>
              
              <Grid container spacing={3}>
                {content.features.items.map((feature, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <FeatureCard>
                      <CardContent sx={{ p: 4 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main',
                            width: 56,
                            height: 56,
                            mb: 2.5
                          }}
                        >
                          {getIconComponent(feature.icon, { 
                            size: 28,
                            style: { color: theme.palette.common.white } 
                          })}
                        </Avatar>
                        
                        <Typography 
                          variant="h6" 
                          component="h3" 
                          sx={{ 
                            mb: 1.5,
                            fontWeight: 600
                          }}
                        >
                          {feature.title}
                        </Typography>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                        >
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          {/* Capabilities Section */}
          <Box 
            id="capabilities"
            sx={{
              py: 10,
              bgcolor: 'background.paper',
            }}
          >
            <Container maxWidth="lg">
              <Typography 
                variant="h2" 
                align="center" 
                sx={{ 
                  mb: 2,
                  background: `linear-gradient(45deg, ${clientData.theme.primary_color}, ${clientData.theme.secondary_color || clientData.theme.primary_color})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                {content.capabilities.title}
              </Typography>
              
              <Typography 
                variant="h5" 
                align="center" 
                color="text.secondary" 
                sx={{ mb: 8 }}
              >
                {content.capabilities.subtitle}
              </Typography>
              
              <Grid container spacing={4}>
                {content.capabilities.items.map((capability, idx) => (
                  <Grid item xs={12} md={6} key={idx}>
                    <FeatureCard sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: 'primary.main',
                              width: 48,
                              height: 48,
                              mr: 2
                            }}
                          >
                            {getIconComponent(capability.icon, { 
                              size: 24,
                              style: { color: theme.palette.common.white } 
                            })}
                          </Avatar>
                          <Typography 
                            variant="h5" 
                            component="h3" 
                            sx={{ fontWeight: 600 }}
                          >
                            {capability.title}
                          </Typography>
                        </Box>
                        
                        <Typography 
                          variant="body1" 
                          color="text.secondary" 
                          sx={{ mb: 3 }}
                        >
                          {capability.description}
                        </Typography>
                        
                        <List sx={{ mt: 'auto' }}>
                          {capability.benefits.map((benefit, bidx) => (
                            <ListItem 
                              key={bidx} 
                              sx={{ 
                                px: 0,
                                py: 0.75
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Check 
                                  size={20}
                                  style={{ 
                                    color: theme.palette.primary.main,
                                    strokeWidth: 3
                                  }} 
                                />
                              </ListItemIcon>
                              <ListItemText 
                                primary={benefit}
                                primaryTypographyProps={{
                                  sx: { fontWeight: 500 }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          {/* Solutions Section */}
          <Box 
            id="solutions"
            sx={{ 
              py: 10,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 100%)'
            }}
          >
            <Container maxWidth="lg">
              <Typography 
                variant="h2" 
                align="center" 
                sx={{ 
                  mb: 2,
                  background: `linear-gradient(45deg, ${clientData.theme.primary_color}, ${clientData.theme.secondary_color || clientData.theme.primary_color})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                {content.solutions.title}
              </Typography>
              
              <Typography 
                variant="h5" 
                align="center" 
                color="text.secondary" 
                sx={{ mb: 8 }}
              >
                {content.solutions.subtitle}
              </Typography>
              
              <Grid container spacing={4}>
                {content.solutions.items.map((solution, idx) => (
                  <Grid item xs={12} md={6} key={idx}>
                    <FeatureCard sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography 
                          variant="h5" 
                          component="h3" 
                          sx={{ 
                            mb: 2,
                            fontWeight: 600
                          }}
                        >
                          {solution.title}
                        </Typography>
                        
                        <Typography 
                          variant="body1" 
                          color="text.secondary" 
                          sx={{ mb: 3 }}
                        >
                          {solution.description}
                        </Typography>
                        
                        <List sx={{ mt: 'auto' }}>
                          {solution.benefits.map((benefit, bidx) => (
                            <ListItem 
                              key={bidx} 
                              sx={{ 
                                px: 0,
                                py: 0.75
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Check 
                                  size={20}
                                  style={{ 
                                    color: theme.palette.primary.main,
                                    strokeWidth: 3
                                  }} 
                                />
                              </ListItemIcon>
                              <ListItemText 
                                primary={benefit}
                                primaryTypographyProps={{
                                  sx: { fontWeight: 500 }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          {/* Security Section */}
          <Box 
            id="security"
            sx={{ 
              py: 10,
              bgcolor: 'background.paper',
            }}
          >
            <Container maxWidth="lg">
              <Typography 
                variant="h2" 
                align="center" 
                sx={{ 
                  mb: 2,
                  background: `linear-gradient(45deg, ${clientData.theme.primary_color}, ${clientData.theme.secondary_color || clientData.theme.primary_color})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                {content.security.title}
              </Typography>
              
              <Typography 
                variant="h5" 
                align="center" 
                color="text.secondary" 
                sx={{ mb: 8 }}
              >
                {content.security.subtitle}
              </Typography>
              
              <Grid container spacing={3}>
                {content.security.items.map((item, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <FeatureCard>
                      <CardContent sx={{ p: 4, display: 'flex', alignItems: 'flex-start' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main',
                            width: 48,
                            height: 48,
                            mr: 2,
                            mt: 0.5
                          }}
                        >
                          {getIconComponent(item.icon, { 
                            size: 24,
                            style: { color: theme.palette.common.white } 
                          })}
                        </Avatar>
                        
                        <Box>
                          <Typography 
                            variant="h6" 
                            component="h3" 
                            sx={{ 
                              mb: 1,
                              fontWeight: 600
                            }}
                          >
                            {item.title}
                          </Typography>
                          
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            {item.description}
                          </Typography>
                        </Box>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          {/* CTA Section */}
          <Box sx={{ py: 12, textAlign: 'center' }}>
            <Container maxWidth="md">
              <Typography 
                variant="h3" 
                sx={{ 
                  mb: 3,
                  fontWeight: 700
                }}
              >
                {content.cta.title}
              </Typography>
              
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ mb: 5 }}
              >
                {content.cta.subtitle}
              </Typography>
              
              <StyledButton 
                variant="contained"
                component={Link} 
                to="/register"
                size="large"
                sx={{ px: 5, py: 1.5 }}
              >
                {content.cta.button}
              </StyledButton>
            </Container>
          </Box>

          {/* Footer */}
          <Box 
            component="footer" 
            sx={{ 
              py: 5, 
              bgcolor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <Container maxWidth="lg">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img 
                      src={clientData.logo_url || "../../images/pv_white.png"} 
                      alt={`${clientData.name} Logo`} 
                      style={{ 
                        height: '30px',
                        borderRadius: clientData.client_id === 'cloudiogram' ? '8px' : '0',
                        objectFit: 'contain'
                      }} 
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                      <Typography variant="body2" component="span">
                        {clientData.name}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {clientData.powered_by}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'center' }, mt: { xs: 2, md: 0 } }}>
                  <Typography variant="caption" color="text.secondary">
                    © {new Date().getFullYear()} {clientData.name}. {content.footer.copyright}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4} sx={{ 
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  mt: { xs: 2, md: 0 },
                  gap: 3
                }}>
                  <MuiLink href="#" color="inherit" sx={{ typography: 'body2' }}>
                    {content.footer.links.privacy}
                  </MuiLink>
                  <MuiLink href="#" color="inherit" sx={{ typography: 'body2' }}>
                    {content.footer.links.terms}
                  </MuiLink>
                  <MuiLink href="#" color="inherit" sx={{ typography: 'body2' }}>
                    {content.footer.links.contact}
                  </MuiLink>
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default WhiteLabelLandingPage;
